import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import Section from '../components/Section';
import { useModalContext } from '../components/ModalProvider';
import {
    getNotificationPreferences,
    NotificationPreferences,
    updateNotificationPreferences,
} from '../PushAPI';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

const T = 'settingsPanel.notificationPreferences.';

type NotificationType = keyof NotificationPreferences['types'];

const TYPES: NotificationType[] = ['alert', 'task', 'broadcast'];

/** The browser's time zone (an IANA name such as `Europe/Berlin`); empty when it cannot be determined. */
export const browserTimeZone = (): string => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch {
        return '';
    }
};

/** Whether `timeZone` is an IANA name the browser knows. */
export const isTimeZone = (timeZone: string): boolean => {
    try {
        new Intl.DateTimeFormat('de', { timeZone });

        return timeZone.trim() !== '';
    } catch {
        return false;
    }
};

/** The zone the server reports for preferences that were never stored. */
export const SERVER_DEFAULT_TIME_ZONE = 'Europe/Berlin';

/**
 * What the form edits: the server's preferences, with the browser's time zone filled in where the
 * server has none, or where it only reports its default (`Europe/Berlin`) for quiet hours that are
 * still off. A zone stored together with enabled quiet hours is never replaced.
 */
export const withDefaultTimeZone = (preferences: NotificationPreferences): NotificationPreferences => {
    const stored = preferences.quietHours?.timezone?.trim() ?? '';
    const browser = browserTimeZone();
    const serverDefault = stored === SERVER_DEFAULT_TIME_ZONE && preferences.quietHours?.enabled !== true;

    return {
        ...preferences,
        quietHours: {
            ...preferences.quietHours,
            timezone: stored === '' || (serverDefault && isTimeZone(browser)) ? browser : preferences.quietHours.timezone,
        },
    };
};

const same = (a: NotificationPreferences, b: NotificationPreferences) => JSON.stringify(a) === JSON.stringify(b);

/**
 * "Benachrichtigungen" in the user profile: which kinds of push the signed-in user wants (alerts, tasks,
 * broadcasts) and quiet hours. The choice only affects the push; the inbox in the app keeps everything.
 * Saved explicitly with the button.
 */
export default function ProfileNotificationPreferences() {
    const { t } = useTranslation();
    const { pushError, pushSuccess } = useModalContext() as any;
    const [saved, setSaved] = useState<NotificationPreferences | null>(null);
    const [draft, setDraft] = useState<NotificationPreferences | null>(null);
    const [failed, setFailed] = useState(false);
    const [saving, setSaving] = useState(false);
    // The last zone that was valid, sent in place of a half-typed one while quiet hours are off.
    const lastValidZone = useRef(SERVER_DEFAULT_TIME_ZONE);

    const load = useCallback(() => {
        let cancelled = false;

        setFailed(false);
        getNotificationPreferences()
            .then((preferences) => {
                if (cancelled) return;

                const prepared = withDefaultTimeZone(preferences);

                if (isTimeZone(prepared.quietHours.timezone)) {
                    lastValidZone.current = prepared.quietHours.timezone;
                }
                setSaved(prepared);
                setDraft(prepared);
            })
            .catch((error) => {
                if (cancelled) return;
                setFailed(true);
                pushError(error);
            });

        return () => {
            cancelled = true;
        };
        // `pushError` comes from a context value that changes identity on render; the preferences load once.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => load(), [load]);

    const quiet = draft?.quietHours;
    const timeZoneProblem = quiet?.enabled === true && !isTimeZone(quiet.timezone);
    const timesMissing = quiet?.enabled === true && (!quiet.from || !quiet.to);
    const sameTimes = quiet?.enabled === true && !timesMissing && quiet.from === quiet.to;
    const dirty = draft !== null && saved !== null && !same(draft, saved);

    const save = async () => {
        if (!draft) {
            return;
        }

        // With quiet hours off the zone is not shown, so an unusable one must not reach the server.
        const zone = isTimeZone(draft.quietHours.timezone) ? draft.quietHours.timezone : lastValidZone.current;
        const payload: NotificationPreferences = { ...draft, quietHours: { ...draft.quietHours, timezone: zone } };

        setSaving(true);
        try {
            // The server's answer is shown as it is: what was just saved is not a default to replace.
            const result = await updateNotificationPreferences(payload);

            setSaved(result);
            setDraft(result);
            pushSuccess?.(t(`${T}saved`));
        } catch (error) {
            pushError(error);
        } finally {
            setSaving(false);
        }
    };

    const changeType = (type: NotificationType, value: boolean) =>
        setDraft((previous) => (previous ? { ...previous, types: { ...previous.types, [type]: value } } : previous));

    const changeQuiet = (change: Partial<NotificationPreferences['quietHours']>) => {
        if (change.timezone !== undefined && isTimeZone(change.timezone)) {
            lastValidZone.current = change.timezone;
        }

        setDraft((previous) =>
            previous ? { ...previous, quietHours: { ...previous.quietHours, ...change } } : previous,
        );
    };

    return (
        <Section title={t(`${T}title`)}>
            <p className="text-sm text-muted-foreground">{t(`${T}intro`)}</p>

            {draft === null && !failed && (
                <p role="status" className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
                    {t(`${T}loading`)}
                </p>
            )}

            {failed && (
                <div className="mt-4 flex flex-wrap items-center gap-3">
                    <p role="alert" className="text-sm text-destructive">{t(`${T}loadFailed`)}</p>
                    <Button variant="outline" size="sm" onClick={() => load()}>
                        {t(`${T}retry`)}
                    </Button>
                </div>
            )}

            {draft && quiet && (
                <div className="mt-4 flex flex-col gap-4">
                    <div className="flex flex-col gap-3" data-guide-id="settings.userProfile.notifications">
                        {TYPES.map((type) => (
                            <div key={type} className="flex items-start gap-3">
                                <Switch
                                    id={`notification-type-${type}`}
                                    checked={draft.types[type]}
                                    onCheckedChange={(value) => changeType(type, value)}
                                />
                                <div className="flex min-w-0 flex-col gap-0.5">
                                    <Label htmlFor={`notification-type-${type}`}>{t(`${T}types.${type}.label`)}</Label>
                                    <p className="text-xs text-muted-foreground">{t(`${T}types.${type}.hint`)}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="flex flex-col gap-3 border-t pt-4">
                        <div className="flex items-start gap-3">
                            <Switch
                                id="notification-quiet-enabled"
                                checked={quiet.enabled}
                                onCheckedChange={(value) => changeQuiet({ enabled: value })}
                            />
                            <div className="flex min-w-0 flex-col gap-0.5">
                                <Label htmlFor="notification-quiet-enabled">{t(`${T}quiet.label`)}</Label>
                                <p className="text-xs text-muted-foreground">{t(`${T}quiet.hint`)}</p>
                            </div>
                        </div>
                        {quiet.enabled && (
                            <div className="flex flex-wrap items-end gap-3 pl-12">
                                <div className="flex flex-col gap-1.5">
                                    <Label htmlFor="notification-quiet-from">{t(`${T}quiet.from`)}</Label>
                                    <Input
                                        id="notification-quiet-from"
                                        type="time"
                                        className="w-32"
                                        value={quiet.from}
                                        onChange={(event) => changeQuiet({ from: event.target.value })}
                                    />
                                </div>
                                <div className="flex flex-col gap-1.5">
                                    <Label htmlFor="notification-quiet-to">{t(`${T}quiet.to`)}</Label>
                                    <Input
                                        id="notification-quiet-to"
                                        type="time"
                                        className="w-32"
                                        value={quiet.to}
                                        onChange={(event) => changeQuiet({ to: event.target.value })}
                                    />
                                </div>
                                <div className="flex min-w-[12rem] flex-1 flex-col gap-1.5">
                                    <Label htmlFor="notification-quiet-zone">{t(`${T}quiet.timezone`)}</Label>
                                    <Input
                                        id="notification-quiet-zone"
                                        value={quiet.timezone}
                                        aria-invalid={timeZoneProblem}
                                        onChange={(event) => changeQuiet({ timezone: event.target.value })}
                                    />
                                </div>
                            </div>
                        )}
                        {quiet.enabled && timeZoneProblem && (
                            <p role="alert" className="pl-12 text-xs text-destructive">{t(`${T}quiet.timezoneInvalid`)}</p>
                        )}
                        {quiet.enabled && timesMissing && (
                            <p role="alert" className="pl-12 text-xs text-destructive" data-testid="quiet-times-missing">
                                {t(`${T}quiet.timesMissing`)}
                            </p>
                        )}
                        {quiet.enabled && sameTimes && (
                            <p role="status" className="pl-12 text-xs text-destructive" data-testid="quiet-times-same">
                                {t(`${T}quiet.sameTimes`)}
                            </p>
                        )}
                        {quiet.enabled && !timeZoneProblem && !timesMissing && !sameTimes && (
                            <p className="pl-12 text-xs text-muted-foreground">{t(`${T}quiet.overnight`)}</p>
                        )}
                    </div>

                    <div className="flex justify-end">
                        <Button disabled={!dirty || saving || timeZoneProblem || timesMissing} onClick={save}>
                            {saving && <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" />}
                            {t(`${T}save`)}
                        </Button>
                    </div>
                </div>
            )}
        </Section>
    );
}
